import NextHead from 'next/head';
import { useMemo } from 'react';

import { getGlobalJsonld } from '~/business/App/dto';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';

type JsonLdProps = {
  extraJsonLd?: Record<string, unknown>;
};

// a job description holding "</script>" would otherwise close the tag and inject the rest as HTML
const escapeForScriptTag = (json: string) => json.replace(/</g, '\\u003c');

/**
 * Rendered in the server HTML: crawlers that do not run JavaScript (Bing, job aggregators) read the
 * structured data there, and Google indexes it without waiting for its rendering pass.
 */
export const JsonLdScript = ({ id, data }: { id: string; data: unknown }) => {
  const json = escapeForScriptTag(JSON.stringify(data));

  return (
    <NextHead>
      {/* DOMPurify would mangle JSON: escapeForScriptTag is what keeps the content inside the tag */}
      {/* eslint-disable-next-line local-rules/require-sanitized-html */}
      <script key={id} type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />
    </NextHead>
  );
};

export const JsonLd = ({ extraJsonLd }: JsonLdProps) => {
  const { businessUnit } = useBusinessUnitContext();

  const data = useMemo(() => getGlobalJsonld(businessUnit, extraJsonLd), [businessUnit, extraJsonLd]);

  return <JsonLdScript id="jsonld" data={data} />;
};
